// src/contexts/apicontext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import i18n from "../i18n";
import { enqueue } from "../pages/src/lib/unfurlQueue";
import { normalizeUrl } from "../pages/src/lib/unfurlQueue";
import { gtag } from "../lib/ga";
/**
 * API base URL 設定:
 * - Vite: import.meta.env.VITE_API_BASE
 * - CRA : process.env.REACT_APP_API_URL
 * - 未設定: 同一オリジン
 *   例) https://api.nicelinks.example.com
 */
// ★ CRA向け：import.meta は使わない ★
const _rawBase =
  (typeof process !== "undefined" &&
    process.env &&
    (process.env.REACT_APP_API_URL|| "")) || "";

const API_BASE = _rawBase.replace(/\/+$/, ""); // 末尾スラッシュ除去

const TOKEN_KEY = "nl_token";
// 旧実装との互換（任意）：current-token があれば取り込む
const readInitialToken = () => {
  const t = localStorage.getItem(TOKEN_KEY) || localStorage.getItem("current-token") || "";
  if (t && !localStorage.getItem(TOKEN_KEY)) localStorage.setItem(TOKEN_KEY, t);
  return t;
};
const MY_USERID_KEY = "nl_userId"; // public userId（表示用ID）

const inflightMap = new Map(); // key -> Promise

const makeInflightKey = ({ method, url, isForm, auth }) =>
  `${method || "GET"} ${url} ::form=${!!isForm} ::auth=${!!auth}`;

export const APIContext = createContext(null);
export const ApiContext = APIContext;  
export const useAPI = () => useContext(APIContext);
export const useApi = useAPI;  

/* ----------------------------- 汎用ユーティリティ ----------------------------- */
const buildURL = (path, params) => {
  const u = `${API_BASE}${path}`;
  if (!params || Object.keys(params).length === 0) return u;
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  const qs = sp.toString();
  return qs ? `${u}?${qs}` : u;
};

const isFileLike = (v) =>
  typeof File !== "undefined" && v instanceof File ||
  (typeof Blob !== "undefined" && v instanceof Blob);



/* ----------------------------- API Provider 本体 ----------------------------- */
export function APIProvider({ children }) {
  // StrictMode やスーパーリロードでの二重起動を防ぐためのガード
const didParseTokenFromURLRef = useRef(false);
const bootstrappedForTokenRef = useRef(null); // 同じ token で bootstrapMe を1回だけ
  const [token, setTokenState] = useState(readInitialToken);
  const [myUserId, setMyUserIdState] = useState(() => localStorage.getItem(MY_USERID_KEY) || "");
  const isAuthenticated = !!token;
  const [me, setMe] = useState(null);            // { userId, email, profile: {...} }
const [profile, setProfile] = useState(null);  // { userId, nickname, image, ... }
const [loadingProfile, setLoadingProfile] = useState(false);
const [clientSettings, setClientSettings] = useState(null); // { language, view_sensitive }


/**
 * 未ログイン時はブラウザ言語へ自動切替
 * - URLの ?lang=ja / ?lang=en があればそれを最優先
 * - 次に localStorage の手動保存（nl_lang_pref）を優先
 * - それ以外は navigator.languages / navigator.language から推定
 */
useEffect(() => {
  if (isAuthenticated) return; // ログイン済みはサーバ側設定が優先

  const sp = new URLSearchParams(window.location.search);
  const paramLang = (sp.get("lang") || "").toLowerCase();
  const savedLang = (localStorage.getItem("nl_lang_pref") || "").toLowerCase();

  const raw =
    paramLang ||
    savedLang ||
    (Array.isArray(navigator.languages) && navigator.languages[0]) ||
    navigator.language ||
    "en";

  const pick = raw.startsWith("ja") ? "ja" : "en"; // 対応言語が増えたらここで分岐追加

  if (i18n.language !== pick) i18n.changeLanguage(pick);

  // ?lang= が付いていたら保存（任意）
  if (paramLang) localStorage.setItem("nl_lang_pref", pick);
}, [isAuthenticated]);


  // トークン保存
  const setToken = useCallback((t) => {
    setTokenState(t || "");
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const setMyUserId = useCallback((uid) => {
    setMyUserIdState(uid || "");
    if (uid) localStorage.setItem(MY_USERID_KEY, uid);
    else localStorage.removeItem(MY_USERID_KEY);
  }, []);



  // fetch ラッパ（デフォルトはAuthあり）
  const request = useCallback(
     async (path, { method = "GET", params, body, headers, auth = true, isForm = false } = {}) => {
      const url = buildURL(path, params);
      const h = new Headers(headers || {});
       // GET に Content-Type を付けると CORS preflight(OPTIONS) が走ります
       if (!isForm && String(method || "GET").toUpperCase() !== "GET") {
         h.set("Content-Type", "application/json");
       }
      if (auth) {
          const latest = localStorage.getItem(TOKEN_KEY); // ★ stateのtokenは使わない
          if (latest) h.set("Authorization", `Token ${latest}`);
        }
      const init = {
        method,
        headers: h,
        credentials: "omit", // Token auth のため cookie は不要
      };
      if (body !== undefined && body !== null) {
        init.body = isForm ? body : JSON.stringify(body);
      }
            // ★ GET の重複排除（同一URL + メソッド）
      const key = makeInflightKey({ method, url, isForm, auth });
      if ((method || "GET").toUpperCase() === "GET") {
        const existing = inflightMap.get(key);
        if (existing) return existing;
      }
      const exec = (async () => {
        const res = await fetch(url, init);
        const text = await res.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch { data = text || null; }
        if (!res.ok) {
          if (res.status === 401 && auth) {
            setToken("");
            setMyUserId("");
          }
          const err = new Error((data && (data.detail || data.error)) || `HTTP ${res.status}`);
          err.status = res.status;
          err.data = data;
          throw err;
        }
        return data;
      })();
      if ((method || "GET").toUpperCase() === "GET") {
        inflightMap.set(key, exec);
        try {
          return await exec;
        } finally {
          inflightMap.delete(key);
        }
      }
      return await exec;
    },
    [setMyUserId, setToken] // ← token 依存を外す（関数アイデンティティ安定化）
  );

  
  /* ----------------------------- 認証 ----------------------------- */

  const getMyClientSettings = useCallback(async () => {
      const cs = await request("/users/me/client-settings/");
      setClientSettings(cs || null);
      try { if (i18n && cs?.language && i18n.language !== cs.language) i18n.changeLanguage(cs.language); } catch {}
      return cs;
    }, [request]);
  
    const updateMyClientSettings = useCallback(async (partial) => {
        // 楽観更新
        setClientSettings((prev) => ({ ...(prev || {}), ...(partial || {}) }));
        return request("/users/me/client-settings/", { method: "PATCH", body: partial });
      }, [request]);
      
      const setClientSettingsLocal = useCallback((partial) => {
        setClientSettings((prev) => ({ ...(prev || {}), ...(partial || {}) }));
      }, []);
  
  // ★ 追加: /me/ を叩いて自分のプロフィールをロード
  const bootstrapMe = useCallback(async (overrideToken) => {
    const useToken = overrideToken ?? token;
    if (!useToken) {
      setMe(null);
      setProfile(null);
      setClientSettings(null);   // ★ クリア
      return;
    }
    setLoadingProfile(true);
    try {
      // ここだけ“直渡しトークン”で叩く
      const data = await request("/me/", {
        auth: false, // ← auth:false にして…
        headers: { Authorization: `Token ${useToken}` }, // ← 直接付ける
      });
      setMe(data);
      setProfile(data?.profile || null);
      if (data?.userId) setMyUserId(data.userId);
  
      // ★ クライアント設定（言語 / センシ閲覧）を取得
      try {
        const cs = await getMyClientSettings();
        setClientSettings(cs || null);
  
        // 言語を反映（未指定なら ja）
        const lang = (cs && cs.language) || "ja";
        if (i18n.language !== lang) i18n.changeLanguage(lang);
      } catch {/* noop */}
  
      // （任意）通知設定も先読みしたいなら：
      // try { await getUserNotificationSettings(data.userId); } catch {}
  
    } catch (e) {
      if (e.status === 401) {
        setToken("");
        setMyUserId("");
        setMe(null);
        setProfile(null);
        setClientSettings(null);
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [token, request, setToken, setMyUserId, getMyClientSettings /*, getUserNotificationSettings*/]);
  

  const loginWithEmail = useCallback(
    async (email, password) => {
      const data = await request("/auth/login/", {
        method: "POST",
        auth: false,
        body: { email, password },
      });
      setToken(data.token);
      // 自分の public userId を取得（PATCH 空ボディでOK：バックエンドがオブジェクトを返す）
      await bootstrapMe(data.token);
      return data;
    },
    [request, setToken, bootstrapMe]
  );

  const logout = useCallback(async () => {
    if (token) {
      try {
        await request("/auth/logout/", { method: "POST" });
      } catch {
        // 失敗してもクライアント側はトークン破棄
      }
    }
    setToken("");
    setMyUserId("");
    setMe(null);
    setProfile(null);
    setClientSettings(null);
  }, [request, token, setToken, setMyUserId]);

  // 既存の仕様準拠：/auth/social/ は email+provider で作成/ログイン
  const loginSocialGeneric = useCallback(
    async (email, provider) => {
      const data = await request("/auth/social/", {
        method: "POST",
        auth: false,
        body: { email, provider },
      });
      setToken(data.token);
      try {
        const me = await request("/users/me/profile/", { method: "PATCH", body: {} });
        setMyUserId(me.userId);
      } catch {}
      return data;
    },
    [request, setToken, setMyUserId]
  );

  // 追加：メール新規登録
const registerWithEmail = useCallback(
  async (email, password) => {
    const data = await request("/auth/register/", {
      method: "POST",
      auth: false,
      body: { email, password },
    });
    // 成功時はログイン状態にしてしまう（バックエンドが token 返す仕様）
    setToken(data.token);
    await bootstrapMe(data.token);
    return data;
  },
  [request, setToken, bootstrapMe]
);

  // Google / LINE
  const loginWithGoogleIdToken = useCallback(
    async ({ id_token, nonce }) => {
      const data = await request("/auth/google/", {
        method: "POST",
        auth: false,
        body: { id_token, nonce },
      });
      setToken(data.token);
      await bootstrapMe(data.token);
      return data;
    },
    [request, setToken, bootstrapMe]
  );

  const loginWithGoogleCode = useCallback(
    async ({ code, redirect_uri, nonce }) => {
      const data = await request("/auth/google/", {
        method: "POST",
        auth: false,
        body: { code, redirect_uri, nonce },
      });
      setToken(data.token);
      localStorage.setItem(TOKEN_KEY, data.token);
      await bootstrapMe(data.token);
      return data;
    },
    [request, setToken, bootstrapMe]
  );

  const loginWithLineCode = useCallback(
    async ({ code, redirect_uri, nonce }) => {
      const data = await request("/auth/line/", {
        method: "POST",
        auth: false,
        body: { code, redirect_uri, nonce },
      });
      setToken(data.token);
      localStorage.setItem(TOKEN_KEY, data.token); // 念のため即書き込み
      await bootstrapMe(data.token);               // 直渡しで /me/ へ
      return data;
    },
    [request, setToken, bootstrapMe]
  );

  useEffect(() => {
    if (me?.userId) {
      gtag("set", { user_id: me.userId }); // GA4のuser_id
    }
  }, [me?.userId]);

  /* ----------------------------- 投稿 / タイムライン ----------------------------- */
  const getTimeline = useCallback(
      async ({ count = 0, includeSensitive } = {}) =>
        // auth:true にすると、localStorage に token があれば自動で Authorization が付きます
        // （token が無いゲスト時は何も付かないので、そのまま公開TLとして動作）
        request("/timeline/", { params: { count, includeSensitive }, auth: true }),
      [request]
    );

    // ★ 追加：フォロー専用タイムライン（要ログイン）
const getTimelineFollowing = useCallback(
  async ({ count = 0, includeSensitive } = {}) =>
    request("/timeline/following/", { params: { count, includeSensitive }, auth: true }),
  [request]
);

  const getUserPosts = useCallback(
    async (userId, { count = 0 } = {}) =>
      request(`/users/${encodeURIComponent(userId)}/posts/`, { params: { count } }),
    [request]
  );

  const createPost = useCallback(
    async ({ postText, postUrl = null, sensitiveFlag = false }) =>
      request("/posts/create/", {
        method: "POST",
        body: { postText, postUrl, sensitiveFlag },
      }),
    [request]
  );

  const toggleNice = useCallback(
    async (postId, wantNice) => {
      if (wantNice) return request(`/posts/${postId}/nice/`, { method: "POST" });
      return request(`/posts/${postId}/nice/`, { method: "DELETE" });
    },
    [request]
  );

   const toggleBookmark = useCallback(
       async (postId, wantBookmark) => {
         if (wantBookmark) return request(`/posts/${postId}/bookmarks/`, { method: "POST" });
         return request(`/posts/${postId}/bookmarks/`, { method: "DELETE" });
       },
       [request]
     );
    
     const getMyBookmarks = useCallback(
       async ({ count = 0 } = {}) => request("/users/me/bookmarks/", { params: { count } }),
       [request]
     );

 const getPostById = useCallback(
     async (postId) => request(`/posts/${postId}/`, { auth: true }),
     [request]
   );

  // Repost のトグル（★追加）
const toggleRepost = useCallback(
  async (postId, wantRepost) => {
    if (wantRepost) return request(`/posts/${postId}/reposts/`, { method: "POST" });
    return request(`/posts/${postId}/reposts/`, { method: "DELETE" });
  },
  [request]
);

// 投稿削除（★追加：本人のみサーバ側で許可）
const deletePost = useCallback(
  async (postId) => request(`/posts/${postId}/delete/`, { method: "DELETE" }),
  [request]
);

  const getUserNices = useCallback(
    async (userId, { count = 0 } = {}) =>
      request(`/users/${encodeURIComponent(userId)}/nices/`, { params: { count } }),
    [request]
  );

  const getPublicTimeline = useCallback(
      async ({ count = 0, includeSensitive } = {}) =>
        request("/timeline/", { params: { count, includeSensitive }, auth: false }),
      [request]
    );

const unfurlUrl = useCallback(
    async (rawUrl) => {
      const u = normalizeUrl(rawUrl);
      return enqueue(
        `unf:${u}`,
        () => request("/utils/unfurl/", { params: { url: u }, auth: false }),
        { cacheKey: u, ttlMs: 7 * 24 * 60 * 60 * 1000 } // 7日
      );
    },
    [request]
  );

  const imageProxyUrl = useCallback(
    (url) => `${API_BASE}/utils/img-proxy/?url=${encodeURIComponent(url)}`,
    []
  );

  /* ----------------------------- コメント / リポスト----------------------------- */
  const getPostComments = useCallback(
    async (postId, { count = 0 } = {}) =>
      request(`/posts/${postId}/comments/`, { params: { count } }),
    [request]
  );

  const addComment = useCallback(
    async (postId, commentText) =>
      request(`/posts/${postId}/comments/`, { method: "POST", body: { commentText } }),
    [request]
  );

  const getPostReposts = useCallback(
    async (postId, { count = 0 } = {}) =>
      request(`/posts/${postId}/reposts/`, { params: { count } }),
    [request]
  );

  const repost = useCallback(
    async (postId) => request(`/posts/${postId}/reposts/`, { method: "POST" }),
    [request]
  );

  /* ----------------------------- フォロー ----------------------------- */
  const follow = useCallback(
    async (userId) => request(`/users/${encodeURIComponent(userId)}/follow/`, { method: "POST" }),
    [request]
  );
  const unfollow = useCallback(
    async (userId) => request(`/users/${encodeURIComponent(userId)}/follow/`, { method: "DELETE" }),
    [request]
  );

  const getFollowing = useCallback(
    async (userId, { count = 0 } = {}) =>
      request(`/users/${encodeURIComponent(userId)}/following/`, { params: { count } }),
    [request]
  );
  const getFollowers = useCallback(
    async (userId, { count = 0 } = {}) =>
      request(`/users/${encodeURIComponent(userId)}/followers/`, { params: { count } }),
    [request]
  );

  /* ----------------------------- プロフィール / 設定 ----------------------------- */
  const getProfile = useCallback(
    async (userId) => request(`/users/${encodeURIComponent(userId)}/profile/`),
    [request]
  );

  const updateMyProfile = useCallback(
    async ({ nickname, birthday, caption, image } = {}) => {
      // 画像がある場合は multipart
      const hasFile = image && isFileLike(image);
      if (hasFile) {
        const fd = new FormData();
        if (nickname !== undefined) fd.append("nickname", nickname);
        if (birthday !== undefined) fd.append("birthday", birthday);
        if (caption !== undefined) fd.append("caption", caption);
        fd.append("image", image);
        return request("/users/me/profile/", { method: "PATCH", body: fd, isForm: true });
      }
      // JSON
      const body = {};
      if (nickname !== undefined) body.nickname = nickname;
      if (birthday !== undefined) body.birthday = birthday;
      if (caption !== undefined) body.caption = caption;
      return request("/users/me/profile/", { method: "PATCH", body });
    },
    [request]
  );

  const getUserNotificationSettings = useCallback(
    async (userId) =>
      request(`/users/${encodeURIComponent(userId)}/notification-settings/`, { auth: false }),
    [request]
  );

  const updateMyNotificationSettings = useCallback(
    async (partial) =>
      request("/users/me/notification-settings/", { method: "PATCH", body: partial }),
    [request]
  );



  /* ----------------------------- 通知 ----------------------------- */
  const getNotifications = useCallback(
    async (userId, { count = 0 } = {}) =>
      request(`/users/${encodeURIComponent(userId)}/notifications/`, { params: { count } }),
    [request]
  );

  const markNotificationRead = useCallback(
    async (notiId) => request(`/notifications/${notiId}/read/`, { method: "POST" }),
    [request]
  );

  const getUnreadNotificationsCount = useCallback(
      async (userId) =>
        request(`/users/${encodeURIComponent(userId)}/notifications/unread_count/`),
      [request]
    );

  /* ----------------------------- 検索 ----------------------------- */
  const searchPosts = useCallback(
    async ({ q = "", count = 0 } = {}) =>
      request("/search/posts/", { params: { q, count } }),
    [request]
  );
  const searchProfiles = useCallback(
    async ({ q = "", count = 0 } = {}) =>
      request("/search/profiles/", { params: { q, count } }),
    [request]
  );

  /* ----------------------------- 広告 ----------------------------- */
  const listAds = useCallback(
      async ({ count = 0, type, target } = {}) =>
        request("/ads/", { params: { count, type, target }, auth: false }),
      [request]
    );

    const IMP_TARGETS = [5000, 50000, 100000];
    const CLK_TARGETS = [1000, 3000, 10000];
    
    const createAd = useCallback(
      async ({ url, content, img, billing = "imp", target }) => {
        const fd = new FormData();
        fd.append("type", "1");        // 画像広告固定
        fd.append("billing", billing === "clk" ? "1" : "0"); // 0=imp,1=clk（サーバ整数）
        fd.append("url", url);
        if (content) fd.append("content", content);
        fd.append("img", img);
        fd.append("target", String(target));
        return request("/ads/", { method: "POST", body: fd, isForm: true });
      },
      [request]
    );

  // 管理者のみ
  const allowAd = useCallback(
    async (signedAdsId) =>
      request("/ads/allow/", { method: "POST", body: { adsId: signedAdsId } }),
    [request]
  );

  // 公開API（署名ID必須）
// context/ApiContext.js の中（抜粋）
const incrementAdEvent = useCallback(
  async (signedAdsId, event /* "imp" | "clk" */) =>
    request("/ads/update/", { method: "POST", auth: false, body: { adsId: signedAdsId, event } }),
  [request]
);

// ★ 追加: 待たない・UIをブロックしない送信
const incrementAdEventAsync = useCallback((signedAdsId, event) => {
  try {
    const url = `${API_BASE}/ads/update/`;
    const payload = JSON.stringify({ adsId: signedAdsId, event });

    // 1) sendBeacon が使えれば最優先（タブ遷移中も送れる）
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }

    // 2) フォールバック: keepalive fetch（待たない）
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true, // 可能なら
      credentials: "omit",
    }).catch(() => {});
  } catch { /* noop */ }
}, [API_BASE]);


  const setAdDeleted = useCallback(
    async (signedAdsId) =>
      request("/ads/update/", { method: "POST", auth: false, body: { adsId: signedAdsId, updateDelFlag: 1 } }),
    [request]
  );

  const checkoutAd = useCallback(
    async ({ SITE_URL, data }) =>
      request("/ads/checkout/", { method: "POST", body: { SITE_URL, data } }),
    [request]
  );

  /* ----------------------------- 起動時：トークンがある場合は userId を回収 ----------------------------- */
  // ① URL から token を拾うのは一度だけ
  useEffect(() => {
    if (didParseTokenFromURLRef.current) return;
    didParseTokenFromURLRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (t) {
      setToken(t);
      localStorage.setItem(TOKEN_KEY, t);
      params.delete("token");
      const newUrl = window.location.pathname + (params.toString() ? `?${params}` : "");
      window.history.replaceState(null, "", newUrl);
    }
  }, [setToken]);
  
  // ② token が変わったとき “その token では一度だけ” bootstrapMe を実行
  useEffect(() => {
    if (!token) {
      bootstrappedForTokenRef.current = null;
      return;
    }
    if (bootstrappedForTokenRef.current === token) return;
    bootstrappedForTokenRef.current = token;
    // 最新 token を直渡し（state 同期レース回避）
    bootstrapMe(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // ← bootstrapMe を依存に含めない（関数アイデンティティでのループ防止）


  /* ----------------------------- ブロック / 報告 ----------------------------- */
const blockUser = useCallback(
  async (userId) => request(`/blocks/`, { method: "POST", body: { blocked: userId } }),
  [request]
);

const reportUser = useCallback(
  async (userId, reason = "") =>
    request(`/user-reports/`, { method: "POST", body: { reported: userId, reason } }),
  [request]
);

const reportPost = useCallback(
  async (postId, reason = "") =>
    request(`/post-reports/`, { method: "POST", body: { post: postId, reason } }),
  [request]
);

  const value = useMemo(
    () => ({
      API_BASE,
      // 自分の情報
      me,
      profile,
      loadingProfile,
      refreshProfile: bootstrapMe,
      // 認証
      token,
      isAuthenticated,
      myUserId,
      setToken,
      logout,
      loginWithEmail,
      loginSocialGeneric,
      loginWithGoogleIdToken,
      loginWithGoogleCode,
      loginWithLineCode,
      registerWithEmail, 
      // 投稿
      getTimeline,
      getTimelineFollowing,
      getUserPosts,
      createPost,
      toggleNice,
      toggleRepost,   // ★
      toggleBookmark,
      deletePost, 
      getUserNices,
      getPostById,
      // コメント / リポスト
      getPostComments,
      addComment,
      getPostReposts,
      repost,
      // フォロー
      follow,
      unfollow,
      getFollowing,
      getFollowers,
      // プロフィール / 設定
      getProfile,
      updateMyProfile,
      getUserNotificationSettings,
      setClientSettingsLocal,
      updateMyNotificationSettings,
      getMyBookmarks,
      // 通知
      getNotifications,
      markNotificationRead,
      // 検索
      searchPosts,
      searchProfiles,
      // 広告
      listAds,
      createAd,
      allowAd,
      setAdDeleted,
      checkoutAd,
      // 低レベル access（必要なら直接使う）
      _request: request,
      getPublicTimeline,
      unfurlUrl,
      imageProxyUrl,
      clientSettings,
      getMyClientSettings,
      updateMyClientSettings,
      getUnreadNotificationsCount,
      blockUser,
      reportPost,
      reportUser,
      incrementAdEvent,

    }),
    [
      token,
      isAuthenticated,
      myUserId,
      me,
      profile,
      loadingProfile,
      bootstrapMe,
      setToken,
      logout,
      loginWithEmail,
      loginSocialGeneric,
      loginWithGoogleIdToken,
      loginWithGoogleCode,
      loginWithLineCode,
      registerWithEmail, 
      getTimeline,
      getUserPosts,
      createPost,
      toggleNice,
      toggleBookmark,
      toggleRepost,   // ★
      deletePost, 
      getPostById,
      getUserNices,
      getPostComments,
      addComment,
      getPostReposts,
      repost,
      follow,
      unfollow,
      getFollowing,
      getFollowers,
      getProfile,
      updateMyProfile,
      getUserNotificationSettings,
      updateMyNotificationSettings,
      getMyBookmarks,
      getNotifications,
      markNotificationRead,
      searchPosts,
      searchProfiles,
      listAds,
      createAd,
      allowAd,
      setAdDeleted,
      checkoutAd,
      request,
      getPublicTimeline,
      unfurlUrl,
      imageProxyUrl,
      clientSettings,
      setClientSettingsLocal,
      getMyClientSettings,
      updateMyClientSettings,
      getUnreadNotificationsCount,
      blockUser,
      reportPost,
      reportUser,
      incrementAdEvent
    ]
  );

  return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
}


export default APIProvider;  